import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { User, UserRole } from '../../types/auth';

// Gerarchia ruoli: più alto = più permessi
export const ROLE_HIERARCHY: Record<UserRole, number> = {
  user: 1,
  admin: 2,
  superadmin: 3,
};

// Mappa: ruolo → home area di default
const ROLE_HOME: Record<UserRole, string> = {
  user: '/dashboard',
  admin: '/admin',
  superadmin: '/superadmin',
};

interface RoleGuardProps {
  children: React.ReactNode;
  /** Ruolo minimo richiesto per accedere */
  requiredRole: UserRole;
  /** Dove redirigere se il ruolo è insufficiente (default: home del ruolo utente) */
  redirectTo?: string;
  /**
   * Se true, redirige anche gli utenti con ruolo SUPERIORE al requiredRole
   * verso la loro home area. Utile su /dashboard per non far atterrare
   * admin/superadmin nella dashboard utente.
   * Default: false
   */
  strictRole?: boolean;
}

/**
 * Protegge una route in base al ruolo.
 * Usa una gerarchia: superadmin > admin > user.
 *
 * Con strictRole=true redirige anche i ruoli superiori verso la loro home,
 * impedendo ad admin/superadmin di navigare su /dashboard.
 *
 * Deve essere usato DENTRO PrivateRoute (presuppone utente autenticato).
 */
const RoleGuard: React.FC<RoleGuardProps> = ({
  children,
  requiredRole,
  redirectTo,
  strictRole = false,
}) => {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/" replace />;
  }

  const userLevel = ROLE_HIERARCHY[user.role];
  const requiredLevel = ROLE_HIERARCHY[requiredRole];

  // Ruolo insufficiente → vai alla home del proprio ruolo (o redirectTo esplicito)
  if (userLevel < requiredLevel) {
    return <Navigate to={redirectTo ?? ROLE_HOME[user.role]} replace />;
  }

  // Ruolo superiore su route riservata a ruolo inferiore → vai alla propria home
  if (strictRole && userLevel > requiredLevel) {
    return <Navigate to={ROLE_HOME[user.role]} replace />;
  }

  return <>{children}</>;
};

export default RoleGuard;

// ─── Hook di utilità (opzionale, comodo nei componenti) ──────
/**
 * Verifica se l'utente corrente ha almeno il ruolo indicato.
 * Utile per mostrare/nascondere elementi UI senza redirect.
 *
 * Esempio:
 *   const canManage = useHasRole('admin');
 *   {canManage && <Button>Gestisci</Button>}
 */
export const useHasRole = (requiredRole: UserRole): boolean => {
  const { user } = useAuth();
  if (!user) return false;
  return ROLE_HIERARCHY[user.role] >= ROLE_HIERARCHY[requiredRole];
};

/**
 * Un admin/superadmin può gestire (bloccare/eliminare/reimpostare password)
 * solo utenti con ruolo strettamente inferiore al proprio — mai se stesso,
 * mai un pari o superiore. Rispecchia `assertCanManage` sul backend
 * (AdminController) — qui serve solo per la UI, l'autorizzazione vera
 * è applicata lato server.
 */
export const canManageUser = (actor: User, target: User): boolean =>
  actor.id !== target.id &&
  ROLE_HIERARCHY[target.role] < ROLE_HIERARCHY[actor.role];
